<template>
  <div class="flex flex-col gap-6">

    <!-- Header -->
    <AppPageHeader :title="t('leaveBalance.title')" :description="t('leaveBalance.description')">
      <template #actions>
        <AppButton variant="outline" @click="showBulkUpdateModal = true">
          <Users class="h-4 w-4 mr-2" />{{ t('leaveBalance.bulkUpdate') }}
        </AppButton>
        <AppButton variant="outline" @click="showPolicyModal = true">
          <Settings class="h-4 w-4 mr-2" />{{ t('leaveBalance.leavePolicies') }}
        </AppButton>
        <AppButton variant="outline" @click="exportBalanceReport">
          <Download class="h-4 w-4 mr-2" />{{ t('leaveBalance.export') }}
        </AppButton>
      </template>
    </AppPageHeader>

    <!-- Adjust-used-days info banner -->
    <div class="flex items-start gap-3 p-4 rounded-lg border border-primary/20 bg-primary/5">
      <Edit class="h-4 w-4 text-primary shrink-0 mt-0.5" />
      <p class="text-sm text-foreground">{{ t('leaveBalance.manualEditInfo') }}</p>
    </div>

    <!-- Stat Cards -->
    <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
      <AppStatCard :title="t('leaveBalance.totalEmployees')" :value="employeeBalances.length" :icon="Users" />
      <AppStatCard :title="t('leaveBalance.totalLeaveDays')" :value="totalLeaveDays" :icon="Calendar" />
      <AppStatCard :title="t('leaveBalance.usedLeaveDays')" :value="totalUsedDays" :icon="TrendingUp" />
      <AppStatCard :title="t('leaveBalance.lowBalance')" :value="lowBalanceEmployees" :icon="AlertTriangle" variant="destructive" />
    </div>

    <!-- Filters -->
    <AppCard>
      <div class="p-4 flex flex-wrap gap-3 items-end">
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-muted-foreground">{{ t('leaveBalance.department') }}</label>
          <select v-model="filters.department" class="h-9 rounded-md border border-input bg-background px-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-ring">
            <option value="">{{ t('leaveBalance.allDepartments') }}</option>
            <option v-for="dept in departments" :key="dept.id" :value="dept.id">{{ dept.name }}</option>
          </select>
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-muted-foreground">{{ t('leaveBalance.leaveType') }}</label>
          <select v-model="filters.leaveType" class="h-9 rounded-md border border-input bg-background px-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-ring">
            <option value="">{{ t('leaveBalance.allTypes') }}</option>
            <option v-for="type in leaveTypes" :key="type.id" :value="type.id">{{ type.name }}</option>
          </select>
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-muted-foreground">{{ t('leaveBalance.balanceStatus') }}</label>
          <select v-model="filters.balanceStatus" class="h-9 rounded-md border border-input bg-background px-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-ring">
            <option value="">{{ t('leaveBalance.allBalances') }}</option>
            <option value="low">{{ t('leaveBalance.low') }}</option>
            <option value="medium">{{ t('leaveBalance.medium') }}</option>
            <option value="high">{{ t('leaveBalance.high') }}</option>
          </select>
        </div>
        <AppButton @click="applyFilters">{{ t('leaveBalance.applyFilters') }}</AppButton>
        <AppButton variant="outline" @click="resetFilters">{{ t('leaveBalance.reset') }}</AppButton>
      </div>
    </AppCard>

    <!-- Balance Table -->
    <AppCard>
      <div class="px-4 py-3 border-b border-border flex items-center justify-between gap-3 flex-wrap">
        <p class="text-sm font-medium text-foreground">{{ t('leaveBalance.employeeLeaveBalances') }}</p>
        <div class="relative">
          <Search class="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input type="text" v-model="searchQuery" :placeholder="t('leaveBalance.searchPlaceholder')" class="h-9 pl-9 pr-3 rounded-md border border-input bg-background text-sm w-56 focus:outline-none focus:ring-2 focus:ring-ring" />
        </div>
      </div>

      <div v-if="loading" class="flex items-center justify-center py-12"><AppSpinner size="lg" /></div>
      <AppEmptyState v-else-if="filteredEmployees.length === 0" :icon="Users" :title="t('leaveBalance.noEmployees')" :description="t('leaveBalance.noEmployeesDesc')" />

      <div v-else class="overflow-x-auto">
        <table class="w-full text-sm">
          <thead>
            <tr class="border-b border-border">
              <th class="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">{{ t('leaveBalance.employee') }}</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">Annual</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide hidden md:table-cell">Sick</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide hidden lg:table-cell">Personal</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide hidden lg:table-cell">Emergency</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide">{{ t('leaveBalance.actions') }}</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-border">
            <tr v-for="employee in paginatedEmployees" :key="employee.id" class="hover:bg-muted/30 transition-colors">
              <td class="px-4 py-3 whitespace-nowrap">
                <div class="flex items-center gap-3">
                  <UserAvatar :name="employee.name" size="sm" />
                  <div>
                    <p class="text-sm font-medium text-foreground">{{ employee.name }}</p>
                    <p class="text-xs text-muted-foreground">{{ employee.department }} · {{ employee.employeeId }}</p>
                  </div>
                </div>
              </td>
              <td class="px-4 py-3 whitespace-nowrap">
                <p class="text-sm font-medium text-foreground">{{ employee.leaveBalances.annual.available }}/{{ employee.leaveBalances.annual.total }}</p>
                <p class="text-xs text-muted-foreground">Used: {{ employee.leaveBalances.annual.used }}</p>
                <div class="w-20 bg-muted rounded-full h-1.5 mt-1"><div class="bg-primary h-1.5 rounded-full" :style="{ width: getUsagePercentage(employee.leaveBalances.annual) + '%' }"></div></div>
              </td>
              <td class="px-4 py-3 whitespace-nowrap hidden md:table-cell">
                <p class="text-sm font-medium text-foreground">{{ employee.leaveBalances.sick.available }}/{{ employee.leaveBalances.sick.total }}</p>
                <p class="text-xs text-muted-foreground">Used: {{ employee.leaveBalances.sick.used }}</p>
                <div class="w-20 bg-muted rounded-full h-1.5 mt-1"><div class="bg-green-500 h-1.5 rounded-full" :style="{ width: getUsagePercentage(employee.leaveBalances.sick) + '%' }"></div></div>
              </td>
              <td class="px-4 py-3 whitespace-nowrap hidden lg:table-cell">
                <p class="text-sm font-medium text-foreground">{{ employee.leaveBalances.personal.available }}/{{ employee.leaveBalances.personal.total }}</p>
                <p class="text-xs text-muted-foreground">Used: {{ employee.leaveBalances.personal.used }}</p>
                <div class="w-20 bg-muted rounded-full h-1.5 mt-1"><div class="bg-orange-500 h-1.5 rounded-full" :style="{ width: getUsagePercentage(employee.leaveBalances.personal) + '%' }"></div></div>
              </td>
              <td class="px-4 py-3 whitespace-nowrap hidden lg:table-cell">
                <p class="text-sm font-medium text-foreground">{{ employee.leaveBalances.emergency.available }}/{{ employee.leaveBalances.emergency.total }}</p>
                <p class="text-xs text-muted-foreground">Used: {{ employee.leaveBalances.emergency.used }}</p>
                <div class="w-20 bg-muted rounded-full h-1.5 mt-1"><div class="bg-red-500 h-1.5 rounded-full" :style="{ width: getUsagePercentage(employee.leaveBalances.emergency) + '%' }"></div></div>
              </td>
              <td class="px-4 py-3">
                <div v-if="employee.banks && employee.banks.length > 0" class="flex flex-col gap-1">
                  <button v-for="bank in employee.banks" :key="bank.id"
                    @click="openAdjustUsedDaysModal(employee, bank)"
                    :title="`${bank.bankName || bank.name}: Used ${bank.usedDays || 0} of ${bank.allocatedDays || 0} days`"
                    class="flex items-center justify-between gap-2 text-xs px-2 py-1.5 rounded-md bg-primary/10 text-primary hover:bg-primary/20 transition-colors font-medium">
                    <div class="flex items-center gap-1.5"><Edit class="h-3 w-3" /><span>{{ bank.bankName || bank.name || 'Bank' }}</span></div>
                    <span class="text-[10px] bg-primary/10 px-1 py-0.5 rounded">{{ bank.usedDays || 0 }}d</span>
                  </button>
                </div>
                <span v-else class="text-xs text-muted-foreground italic">No banks</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="totalPages > 1" class="flex items-center justify-between px-4 py-3 border-t border-border">
        <span class="text-sm text-muted-foreground">{{ (currentPage-1)*itemsPerPage+1 }}–{{ Math.min(currentPage*itemsPerPage, totalRecords) }} of {{ totalRecords }}</span>
        <div class="flex items-center gap-1">
          <AppButton size="icon-sm" variant="outline" :disabled="currentPage === 1" @click="previousPage"><ChevronLeft class="h-3.5 w-3.5" /></AppButton>
          <button v-for="p in visiblePages" :key="p" @click="goToPage(p)" :class="['h-8 min-w-[2rem] px-2 rounded-md text-sm font-medium transition-colors', p === currentPage ? 'bg-primary text-primary-foreground' : 'border border-border text-foreground hover:bg-accent']">{{ p }}</button>
          <AppButton size="icon-sm" variant="outline" :disabled="currentPage === totalPages" @click="nextPage"><ChevronRight class="h-3.5 w-3.5" /></AppButton>
        </div>
      </div>
    </AppCard>

    <!-- Edit Balance Modal -->
    <AppModal v-model="showEditModal" :title="`Edit Balance — ${editingEmployee?.name}`" size="md">
      <form @submit.prevent="saveBalance" class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-foreground">Annual Leave</label>
          <input type="number" v-model.number="balanceForm.annual" min="0" max="50" class="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-foreground">Sick Leave</label>
          <input type="number" v-model.number="balanceForm.sick" min="0" max="30" class="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-foreground">Personal Leave</label>
          <input type="number" v-model.number="balanceForm.personal" min="0" max="20" class="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-foreground">Emergency Leave</label>
          <input type="number" v-model.number="balanceForm.emergency" min="0" max="15" class="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
        </div>
        <div class="md:col-span-2 flex flex-col gap-1">
          <label class="text-sm font-medium text-foreground">Adjustment Reason</label>
          <textarea v-model="balanceForm.reason" rows="3" placeholder="Reason for balance adjustment…" class="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"></textarea>
        </div>
      </form>
      <template #footer>
        <div class="flex justify-end gap-2">
          <AppButton variant="outline" @click="closeEditModal">Cancel</AppButton>
          <AppButton :disabled="submitting" @click="saveBalance">{{ submitting ? 'Saving…' : 'Update Balance' }}</AppButton>
        </div>
      </template>
    </AppModal>

    <!-- Leave Policies Modal -->
    <AppModal v-model="showPolicyModal" title="Leave Policies Configuration" size="lg">
      <div class="flex flex-col gap-4">
        <div v-for="leaveType in leaveTypes" :key="leaveType.id" class="p-4 rounded-lg border border-border">
          <h4 class="text-sm font-semibold text-foreground mb-3">{{ leaveType.name }}</h4>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
            <div class="flex flex-col gap-1">
              <label class="text-xs font-medium text-muted-foreground">Annual Allocation</label>
              <input type="number" v-model.number="leaveType.annualAllocation" min="0" class="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-xs font-medium text-muted-foreground">Max Carry Forward</label>
              <input type="number" v-model.number="leaveType.maxCarryForward" min="0" class="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
            </div>
            <div class="flex flex-col gap-1">
              <label class="text-xs font-medium text-muted-foreground">Min Notice (days)</label>
              <input type="number" v-model.number="leaveType.minNotice" min="0" class="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
            </div>
          </div>
          <div class="flex flex-wrap gap-4 text-sm">
            <label class="flex items-center gap-2 cursor-pointer"><input type="checkbox" v-model="leaveType.requiresApproval" class="rounded" /><span>Requires Approval</span></label>
            <label class="flex items-center gap-2 cursor-pointer"><input type="checkbox" v-model="leaveType.allowHalfDay" class="rounded" /><span>Allow Half Day</span></label>
            <label class="flex items-center gap-2 cursor-pointer"><input type="checkbox" v-model="leaveType.allowNegativeBalance" class="rounded" /><span>Allow Negative Balance</span></label>
          </div>
        </div>
      </div>
      <template #footer>
        <div class="flex justify-end gap-2">
          <AppButton variant="outline" @click="showPolicyModal = false">Cancel</AppButton>
          <AppButton :disabled="submitting" @click="savePolicies">{{ submitting ? 'Saving…' : 'Save Policies' }}</AppButton>
        </div>
      </template>
    </AppModal>

    <!-- Bulk Update Modal -->
    <AppModal v-model="showBulkUpdateModal" title="Bulk Update Leave Balances" size="sm">
      <form @submit.prevent="performBulkUpdate" class="flex flex-col gap-4">
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-foreground">Department</label>
          <select v-model="bulkUpdate.department" class="h-9 rounded-md border border-input bg-background px-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-ring">
            <option value="">All Departments</option>
            <option v-for="dept in departments" :key="dept.id" :value="dept.id">{{ dept.name }}</option>
          </select>
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-foreground">Leave Type <span class="text-destructive">*</span></label>
          <select v-model="bulkUpdate.leaveType" required class="h-9 rounded-md border border-input bg-background px-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-ring">
            <option value="">Select leave type</option>
            <option v-for="type in leaveTypes" :key="type.id" :value="type.id">{{ type.name }}</option>
          </select>
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-foreground">Action <span class="text-destructive">*</span></label>
          <select v-model="bulkUpdate.action" required class="h-9 rounded-md border border-input bg-background px-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-ring">
            <option value="">Select action</option>
            <option value="add">Add Days</option>
            <option value="set">Set Balance</option>
            <option value="reset">Reset to Default</option>
          </select>
        </div>
        <div v-if="bulkUpdate.action === 'add' || bulkUpdate.action === 'set'" class="flex flex-col gap-1">
          <label class="text-sm font-medium text-foreground">Days <span class="text-destructive">*</span></label>
          <input type="number" v-model.number="bulkUpdate.days" min="0" required class="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-sm font-medium text-foreground">Reason <span class="text-destructive">*</span></label>
          <textarea v-model="bulkUpdate.reason" rows="3" required placeholder="Reason for bulk update…" class="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"></textarea>
        </div>
      </form>
      <template #footer>
        <div class="flex justify-end gap-2">
          <AppButton variant="outline" @click="showBulkUpdateModal = false">Cancel</AppButton>
          <AppButton :disabled="submitting" @click="performBulkUpdate">{{ submitting ? 'Updating…' : 'Update Balances' }}</AppButton>
        </div>
      </template>
    </AppModal>

    <!-- Adjust Used Days Modal (child component) -->
    <AdjustUsedDaysModal
      v-if="showAdjustUsedDaysModal"
      :employee-id="adjustingEmployee.id"
      :employee-name="adjustingEmployee.name"
      :bank-id="adjustingBank.bankId"
      :bank-name="adjustingBank.bankName || adjustingBank.name || 'Bank'"
      :current-used-days="adjustingBank.usedDays || 0"
      :year="adjustingBank.year || currentYear"
      @close="closeAdjustUsedDaysModal"
      @submit="handleAdjustUsedDays"
    />

  </div>
</template>
<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  Users,
  Calendar,
  TrendingUp,
  AlertTriangle,
  Settings,
  Download,
  Search,
  Edit,
  ChevronLeft,
  ChevronRight,
  X
} from 'lucide-vue-next';
import dayjs from 'dayjs';
import UserAvatar from '@/components/common/UserAvatar.vue';
import AdjustUsedDaysModal from '@/components/admin/balanceBanks/AdjustUsedDaysModal.vue';
import { useToast } from '@/composables/useToast';

// Import services
import attendanceService from '@/services/attendance';
import adminService from '@/services/adminService';
import balanceBankService from '@/services/balanceBankService';
import { AppPageHeader, AppCard, AppBadge, AppButton, AppModal, AppEmptyState, AppSpinner, AppStatCard } from '@/components/ui';

const { t } = useI18n();

// Toast notifications
const toast = useToast();

// Reactive data
const loading = ref(false);
const submitting = ref(false);
const searchQuery = ref('');
const currentPage = ref(1);
const itemsPerPage = ref(20);
const showEditModal = ref(false);
const showPolicyModal = ref(false);
const showBulkUpdateModal = ref(false);
const showAdjustUsedDaysModal = ref(false);
const editingEmployee = ref(null);
const adjustingEmployee = ref(null);
const adjustingBank = ref(null);
const currentYear = ref(new Date().getFullYear());

const employeeBalances = ref([]);
const departments = ref([]);
const leaveTypes = ref([]);

const filters = ref({
  department: '',
  leaveType: '',
  balanceStatus: ''
});

const balanceForm = ref({
  annual: 0,
  sick: 0,
  personal: 0,
  emergency: 0,
  reason: ''
});

const bulkUpdate = ref({
  department: '',
  leaveType: '',
  action: '',
  days: 0,
  reason: ''
});

// Computed properties
const filteredEmployees = computed(() => {
  let filtered = employeeBalances.value;

  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    filtered = filtered.filter(emp => 
      emp.name.toLowerCase().includes(query) ||
      emp.employeeId.toLowerCase().includes(query) ||
      emp.department.toLowerCase().includes(query)
    );
  }

  if (filters.value.department) {
    filtered = filtered.filter(emp => emp.departmentId === filters.value.department);
  }

  if (filters.value.balanceStatus) {
    filtered = filtered.filter(emp => {
      const total = getTotalAvailableLeave(emp);
      switch (filters.value.balanceStatus) {
        case 'low': return total < 5;
        case 'medium': return total >= 5 && total <= 15;
        case 'high': return total > 15;
        default: return true;
      }
    });
  }

  return filtered;
});

const totalRecords = computed(() => filteredEmployees.value.length);
const totalPages = computed(() => Math.ceil(totalRecords.value / itemsPerPage.value));

const paginatedEmployees = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value;
  const end = start + itemsPerPage.value;
  return filteredEmployees.value.slice(start, end);
});

const visiblePages = computed(() => {
  const pages = [];
  const start = Math.max(1, currentPage.value - 2);
  const end = Math.min(totalPages.value, start + 4);
  
  for (let i = start; i <= end; i++) {
    pages.push(i);
  }
  
  return pages;
});

const totalLeaveDays = computed(() => {
  return employeeBalances.value.reduce((total, emp) => {
    // Sum unique BANK totals to avoid counting shared banks multiple times
    if (emp.banks && emp.banks.length > 0) {
      return total + emp.banks.reduce((bankTotal, bank) => {
        return bankTotal + (bank.allocatedDays || 0) + (bank.carriedOverDays || 0) + (bank.bonusDays || 0);
      }, 0);
    }

    // Fallback for legacy system
    return total +
      emp.leaveBalances.annual.total +
      emp.leaveBalances.sick.total +
      emp.leaveBalances.personal.total +
      emp.leaveBalances.emergency.total;
  }, 0);
});

const totalUsedDays = computed(() => {
  return employeeBalances.value.reduce((total, emp) => {
    // Sum unique BANK used days to avoid counting shared banks multiple times
    if (emp.banks && emp.banks.length > 0) {
      return total + emp.banks.reduce((bankTotal, bank) => {
        return bankTotal + (bank.usedDays || 0);
      }, 0);
    }

    // Fallback for legacy system
    return total +
      emp.leaveBalances.annual.used +
      emp.leaveBalances.sick.used +
      emp.leaveBalances.personal.used +
      emp.leaveBalances.emergency.used;
  }, 0);
});

const lowBalanceEmployees = computed(() => {
  return employeeBalances.value.filter(emp => getTotalAvailableLeave(emp) < 5).length;
});

// Methods
const loadEmployeeBalances = async () => {
  try {
    loading.value = true;
    console.log('📊 Loading employee leave balances...');

    const response = await attendanceService.leave.admin.getLeaveBalances();
    const responseData = response.data?.data || {};

    console.log('📦 API Response:', responseData);

    // Check if using new Balance Banks system
    if (responseData.usingBalanceBanks && responseData.employees) {
      console.log('✅ Using Balance Banks system');

      // Transform bank-based balances to employee structure
      employeeBalances.value = responseData.employees.map(empData => {
        const employee = {
          id: empData.employee.id,
          name: empData.employee.name,
          employeeId: empData.employee.employeeCode || `EMP${empData.employee.id.toString().padStart(3, '0')}`,
          department: empData.employee.department || 'N/A',
          departmentId: empData.employee.departmentId || null,
          avatar: null,
          banks: empData.banks || [],
          leaveBalances: {
            annual: { total: 0, used: 0, available: 0 },
            sick: { total: 0, used: 0, available: 0 },
            personal: { total: 0, used: 0, available: 0 },
            emergency: { total: 0, used: 0, available: 0 }
          }
        };

        // Map banks to leave type structure for backward compatibility
        empData.banks.forEach(bank => {
          // For each leave type in the bank, share the bank's balance
          const bankBalance = {
            total: bank.allocatedDays + bank.carriedOverDays + bank.bonusDays,
            used: bank.usedDays + bank.pendingDays,
            available: bank.remainingDays
          };

          bank.leaveTypes.forEach(leaveType => {
            const code = leaveType.code.toLowerCase();
            if (employee.leaveBalances[code] !== undefined) {
              // If multiple types share a bank, they share the balance
              employee.leaveBalances[code] = { ...bankBalance };
            }
          });
        });

        return employee;
      });

      console.log('✅ Employee balances loaded (Balance Banks):', employeeBalances.value.length);
    } else {
      // Legacy system
      console.log('⚠️ Using legacy leave_balances system');
      const balanceRecords = responseData.leaveBalances || [];

      const employeeMap = new Map();

      balanceRecords.forEach(balance => {
        const empId = balance.employee.id;

        if (!employeeMap.has(empId)) {
          employeeMap.set(empId, {
            id: empId,
            name: balance.employee.name,
            employeeId: balance.employee.employeeCode || `EMP${empId.toString().padStart(3, '0')}`,
            department: balance.employee.department || 'N/A',
            departmentId: null,
            avatar: null,
            leaveBalances: {
              annual: { total: 0, used: 0, available: 0 },
              sick: { total: 0, used: 0, available: 0 },
              personal: { total: 0, used: 0, available: 0 },
              emergency: { total: 0, used: 0, available: 0 }
            }
          });
        }

        const employee = employeeMap.get(empId);
        const leaveTypeCode = balance.leaveType.code.toLowerCase();

        if (employee.leaveBalances[leaveTypeCode] !== undefined) {
          employee.leaveBalances[leaveTypeCode] = {
            total: balance.allocatedDays,
            used: balance.usedDays,
            available: balance.remainingDays
          };
        }
      });

      employeeBalances.value = Array.from(employeeMap.values());
      console.log('✅ Employee balances loaded (Legacy):', employeeBalances.value.length);
    }
  } catch (error) {
    console.error('❌ Error loading employee balances:', error);
    console.log('🔄 Loading mock data for development');
    loadMockData();
  } finally {
    loading.value = false;
  }
};

const loadMockData = () => {
  employeeBalances.value = [
    {
      id: 1,
      name: 'John Smith',
      employeeId: 'EMP001',
      department: 'Engineering',
      departmentId: 1,
      avatar: null,
      leaveBalances: {
        annual: { total: 25, used: 8, available: 17 },
        sick: { total: 12, used: 2, available: 10 },
        personal: { total: 5, used: 1, available: 4 },
        emergency: { total: 3, used: 0, available: 3 }
      }
    },
    {
      id: 2,
      name: 'Sarah Johnson',
      employeeId: 'EMP002',
      department: 'Marketing',
      departmentId: 2,
      avatar: null,
      leaveBalances: {
        annual: { total: 20, used: 15, available: 5 },
        sick: { total: 10, used: 3, available: 7 },
        personal: { total: 5, used: 2, available: 3 },
        emergency: { total: 3, used: 1, available: 2 }
      }
    }
  ];
};

const loadDepartments = async () => {
  try {
    console.log('🏢 Loading departments...');
    const departments_list = await adminService.getDepartments();
    departments.value = departments_list.map((name, index) => ({ 
      id: index + 1, 
      name 
    })) || [];
    console.log('✅ Departments loaded:', departments.value.length);
  } catch (error) {
    console.error('❌ Error loading departments:', error);
    departments.value = [
      { id: 1, name: 'Engineering' },
      { id: 2, name: 'Marketing' },
      { id: 3, name: 'Sales' },
      { id: 4, name: 'HR' }
    ];
  }
};

const loadLeaveTypes = async () => {
  try {
    console.log('📋 Loading leave types...');
    const response = await attendanceService.leave.admin.getLeaveTypes();
    leaveTypes.value = response.data?.leaveTypes || [];
    console.log('✅ Leave types loaded:', leaveTypes.value.length);
  } catch (error) {
    console.error('❌ Error loading leave types:', error);
    leaveTypes.value = [
      { 
        id: 'annual', 
        name: 'Annual Leave',
        annualAllocation: 25,
        maxCarryForward: 5,
        minNotice: 7,
        requiresApproval: true,
        allowHalfDay: true,
        allowNegativeBalance: false
      },
      { 
        id: 'sick', 
        name: 'Sick Leave',
        annualAllocation: 12,
        maxCarryForward: 3,
        minNotice: 0,
        requiresApproval: false,
        allowHalfDay: true,
        allowNegativeBalance: true
      },
      { 
        id: 'personal', 
        name: 'Personal Leave',
        annualAllocation: 5,
        maxCarryForward: 2,
        minNotice: 3,
        requiresApproval: true,
        allowHalfDay: false,
        allowNegativeBalance: false
      },
      { 
        id: 'emergency', 
        name: 'Emergency Leave',
        annualAllocation: 3,
        maxCarryForward: 0,
        minNotice: 0,
        requiresApproval: false,
        allowHalfDay: false,
        allowNegativeBalance: false
      }
    ];
  }
};

const editEmployeeBalance = (employee) => {
  editingEmployee.value = employee;
  balanceForm.value = {
    annual: employee.leaveBalances.annual.available,
    sick: employee.leaveBalances.sick.available,
    personal: employee.leaveBalances.personal.available,
    emergency: employee.leaveBalances.emergency.available,
    reason: ''
  };
  showEditModal.value = true;
};

const saveBalance = async () => {
  try {
    submitting.value = true;
    console.log('💾 Updating leave balance for employee:', editingEmployee.value.id);

    await attendanceService.leave.admin.updateLeaveBalance(editingEmployee.value.id, balanceForm.value);

    closeEditModal();
    await loadEmployeeBalances();
    toast.success('Leave balance updated successfully!');
  } catch (error) {
    console.error('❌ Error saving balance:', error);
    toast.error('Failed to update leave balance. Please try again.');
  } finally {
    submitting.value = false;
  }
};

const closeEditModal = () => {
  showEditModal.value = false;
  editingEmployee.value = null;
  balanceForm.value = {
    annual: 0,
    sick: 0,
    personal: 0,
    emergency: 0,
    reason: ''
  };
};

const savePolicies = async () => {
  try {
    submitting.value = true;
    console.log('📋 Updating leave policies...');

    await attendanceService.leave.admin.updateLeavePolicies({
      leaveTypes: leaveTypes.value
    });

    showPolicyModal.value = false;
    toast.success('Leave policies updated successfully!');
  } catch (error) {
    console.error('❌ Error saving policies:', error);
    toast.error('Failed to update leave policies. Please try again.');
  } finally {
    submitting.value = false;
  }
};

const performBulkUpdate = async () => {
  try {
    submitting.value = true;
    console.log('📦 Performing bulk update:', bulkUpdate.value);

    await attendanceService.leave.admin.bulkUpdateLeaveBalances(bulkUpdate.value);

    showBulkUpdateModal.value = false;
    bulkUpdate.value = {
      department: '',
      leaveType: '',
      action: '',
      days: 0,
      reason: ''
    };
    
    await loadEmployeeBalances();
    toast.success('Bulk update completed successfully!');
  } catch (error) {
    console.error('❌ Error performing bulk update:', error);
    toast.error('Failed to perform bulk update. Please try again.');
  } finally {
    submitting.value = false;
  }
};

const exportBalanceReport = async () => {
  try {
    console.log('📤 Exporting leave balance report...');
    
    const response = await attendanceService.leave.admin.exportLeaveBalancesReport({
      ...filters.value,
      format: 'xlsx'
    });

    const blob = new Blob([response.data], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `leave-balances-${dayjs().format('YYYY-MM-DD')}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    console.log('✅ Leave balance report exported successfully');
    toast.success('Leave balance report exported successfully!');
  } catch (error) {
    console.error('❌ Error exporting report:', error);
    toast.error('Failed to export report. Please try again.');
  }
};

const getTotalAvailableLeave = (employee) => {
  // Sum up unique BANK balances, not leave type balances
  // This prevents counting the same bank twice when multiple leave types share one bank
  if (employee.banks && employee.banks.length > 0) {
    return employee.banks.reduce((total, bank) => {
      return total + (bank.remainingDays || 0);
    }, 0);
  }

  // Fallback for legacy system
  return employee.leaveBalances.annual.available +
         employee.leaveBalances.sick.available +
         employee.leaveBalances.personal.available +
         employee.leaveBalances.emergency.available;
};

const getTotalBalanceClass = (employee) => {
  const total = getTotalAvailableLeave(employee);
  if (total < 5) return 'text-red-600';
  if (total < 15) return 'text-orange-600';
  return 'text-green-600';
};

const getUsagePercentage = (balance) => {
  if (balance.total === 0) return 0;
  return (balance.used / balance.total) * 100;
};

const applyFilters = () => {
  currentPage.value = 1;
};

const viewEmployeeHistory = (employee) => {
  // Navigate to employee leave history
  alert(`View leave history for ${employee.name} - Feature coming soon!`);
};

const adjustBalance = (employee) => {
  // Quick adjust balance
  const adjustment = prompt(`Enter adjustment for ${employee.name} (use +/- for add/subtract):`);
  if (adjustment) {
    alert(`Balance adjustment recorded: ${adjustment} days`);
  }
};

// Adjust Used Days Modal Methods
const openAdjustUsedDaysModal = (employee, bank) => {
  adjustingEmployee.value = employee;
  adjustingBank.value = bank;
  showAdjustUsedDaysModal.value = true;
};

const closeAdjustUsedDaysModal = () => {
  showAdjustUsedDaysModal.value = false;
  adjustingEmployee.value = null;
  adjustingBank.value = null;
};

const handleAdjustUsedDays = async (data) => {
  try {
    loading.value = true;
    console.log('📝 Adjusting used days:', data);

    const response = await balanceBankService.adjustUsedDays(
      data.employeeId,
      data.bankId,
      data
    );

    if (response.success) {
      toast.success('Used days adjusted successfully');
      closeAdjustUsedDaysModal();

      // Reload balances to reflect the change
      await loadEmployeeBalances();
    } else {
      toast.error(response.message || 'Failed to adjust used days');
    }
  } catch (error) {
    console.error('❌ Error adjusting used days:', error);
    toast.error(error.response?.data?.message || 'Failed to adjust used days');
  } finally {
    loading.value = false;
  }
};

// Pagination methods
const previousPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
  }
};

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
  }
};

const goToPage = (page) => {
  currentPage.value = page;
};

// Note: Using useToast composable for notifications

const resetFilters = () => {
  filters.value = { department: '', leaveType: '', balanceStatus: '' };
  applyFilters();
};

// Lifecycle
onMounted(() => {
  console.log('🔄 LeaveBalanceManagement mounted, loading data...');
  loadEmployeeBalances();
  loadDepartments();
  loadLeaveTypes();
});
</script>

<style scoped>
.leave-balance-management {
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
}
</style>
